import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { environment } from '../../../../environments/environment';
import { isShapeGroupOrderState2D } from '../../../core/h3ml/state/shape-typing.utils';
import { answerHelper2D } from '../../../core/outputs/annotation/annotation-shared/shape-layer-answer-helper';
import { RasterLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/raster-layer-state';
import { TransientShapeLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/transient-shape-layer-state';
import { VectorLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/vector-layer-state';
import { contentConnectorToken } from '../../../core/outputs/annotation/utils/misc-utils';
import { FeatureFlagService } from '../../../core/shared/misc-services/feature-flag.service';
import { getUrlParam } from '../../../cross-team-shared/misc-utils';
import {
  ImageContentConnector,
  LoadTaskResult,
} from '../../../team-apis/content/content-connector';
import { ImageAnnotationAnswer } from '../../../team-apis/otterdam/answers/image-annotation-answer';
import {
  ImageAnnotationLayerType,
  ImageAnnotationOutput,
} from '../../../team-apis/otterdam/outputs/image-annotation-output';
import {
  AbstractAnnotationOutputComponent,
  contentInjectorToken,
} from '../annotation-shared/abstract-annotation-output-component';
import { annotationProviders2D } from '../annotation-shared/annotation-providers';
import { TransientRasterLayerState } from '../annotation-shared/state/layers/transient-raster-layer-state';
import { AbstractAnnotationStore } from '../annotation-shared/state/root/abstract-annotation-store';
import { AbstractAnnotationStore2D } from '../annotation-shared/state/root/abstract-annotation-store-2d';
import { imageAnnotationShortcuts } from './image-annotation-output-shortcuts';
import { ImageAnnotationState } from './state/image-annotation-state';
import { ImageAnnotationStore } from './state/image-annotation-store';

@Component({
  selector: 'h3-image-annotation-output',
  templateUrl: './image-annotation-output.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    annotationProviders2D,
    ImageAnnotationStore,
    { provide: AbstractAnnotationStore, useExisting: ImageAnnotationStore },
    { provide: AbstractAnnotationStore2D, useExisting: ImageAnnotationStore },
    {
      provide: contentConnectorToken,
      deps: [ImageAnnotationOutputComponent],
      useFactory: (self: ImageAnnotationOutputComponent) =>
        self.contentConnector,
    },
    {
      provide: contentInjectorToken,
      deps: [ImageAnnotationOutputComponent],
      useFactory: (self: ImageAnnotationOutputComponent) =>
        self.contentInjector,
    },
  ],
  standalone: false,
})
export class ImageAnnotationOutputComponent
  extends AbstractAnnotationOutputComponent<
    ImageAnnotationState,
    ImageAnnotationOutput,
    ImageAnnotationAnswer
  >
  implements OnDestroy, OnInit
{
  showAddRandomShapes =
    environment.shouldShowAddRandomShapes ||
    getUrlParam('showAddRandomShapes') === 'true';
  override contentConnector!: ImageContentConnector;
  override featureFlagService: FeatureFlagService;
  // These are tested in the keyboard shortcut service to avoid duplicating most
  // of the tests in the spec for this component.
  protected keyboardShortcuts = imageAnnotationShortcuts;

  private layerType!: ImageAnnotationLayerType;

  constructor(
    injector: Injector,
    public store: ImageAnnotationStore,
  ) {
    super(store, injector);
    if (environment.shouldMakeHackyStoreForImageAnnotationTests) {
      (window as any).imageAnnotationStoreForHackyTest = store;
    }
    this.featureFlagService = injector.get(FeatureFlagService);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.layerType = this.outputInfoStore.state().output.meta.layer_type;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.store.delete();
  }

  protected override setUpSubscriptions() {
    super.setUpSubscriptions();

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.objectlessToolbarKeyboardShortcutService.register2D();
    }
  }

  protected answer() {
    if (this.layerType === 'vector_tagging') {
      return this.getAnswerForVectorTagging();
    }
    // we manage the answer in `prepareForSaveAndSetAnswer()` - keep it the same
    return this.outputInfoStore.state().answer || {};
  }

  protected resetStoreForNewTask(result: LoadTaskResult) {
    const { width, height } = result;
    const state = new ImageAnnotationState();

    const layer = this.createLayers(width, height);
    state.layers = [layer];

    state.status = 'ready';
    this.setSensorRendererState(result);
    this.store.set(state);
    this.resetRightPanelState();
  }

  protected override updateShapeLayer() {
    if (this.layerType === 'raster_coding') {
      this.contentConnector.loadMask(
        this.outputInfoStore.state().answer,
        this.taskWorkspaceStore.state().task._extras,
      );
    }
  }

  protected createLayerState(width: number, height: number) {
    const layer = new VectorLayerState(
      width,
      height,
      new TransientShapeLayerState(),
    );
    return layer;
  }

  private createLayers(width: number, height: number) {
    switch (this.layerType) {
      case 'raster_coding':
        return new RasterLayerState(
          width,
          height,
          new TransientRasterLayerState(),
        );
      case 'vector_tagging':
        return this.createLayerState(width, height);
      default:
        throw new Error('unexpected layer type');
    }
  }

  private getAnswerForVectorTagging(): ImageAnnotationAnswer {
    const shapeGroupOrder =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.annotationOutputName
      ];

    // @ts-ignore -- fix me
    if (isEmpty(shapeGroupOrder.shapes)) {
      return {};
    }

    assert(
      // @ts-ignore -- fix me
      isShapeGroupOrderState2D(shapeGroupOrder),
      'annotation should only have 2d shapes',
    );

    return {
      layers: {
        vector_tagging: answerHelper2D.shapeGroupOrderExternal(shapeGroupOrder),
      },
    };
  }
}
